import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Settings, Users, Palette, Shield, Plus, Edit, Trash2, 
    Upload, Copy, RefreshCw, Check, X, Save, UserPlus, Mail, Key, KeyRound, BarChart3, ClipboardList, LogIn, Code2, Trash, ExternalLink, Eye, EyeOff
} from 'lucide-react';
import AdminTimeStats from '../components/AdminTimeStats';
import BackupSection from './system/BackupSection';
import StorageMigrationSection from './system/StorageMigrationSection';

const ROLE_LABELS = {
    chef: 'Administrator',
    account_manager: 'Account Manager',
    buchhaltung: 'Buchhaltung',
    mitarbeiter: 'Mitarbeiter',
    influencer: 'Influencer',
    freelancer: 'Freelancer'
};

const PERMISSION_LABELS = {
    dashboard_view: 'Dashboard sehen',
    users_view: 'Benutzer sehen',
    users_create: 'Benutzer erstellen',
    users_edit: 'Benutzer bearbeiten',
    users_delete: 'Benutzer löschen',
    tasks_view: 'Tasks sehen',
    tasks_create: 'Tasks erstellen',
    tasks_edit: 'Tasks bearbeiten',
    tasks_delete: 'Tasks löschen',
    projects_view: 'Projekte sehen',
    projects_create: 'Projekte erstellen',
    projects_edit: 'Projekte bearbeiten',
    projects_delete: 'Projekte löschen',
    customers_view: 'Kunden sehen',
    customers_create: 'Kunden erstellen',
    customers_edit: 'Kunden bearbeiten',
    customers_delete: 'Kunden löschen',
    time_view: 'Zeiten sehen',
    time_create: 'Zeiten erfassen',
    time_edit: 'Zeiten bearbeiten',
    time_delete: 'Zeiten löschen',
    vacation_view: 'Urlaub sehen',
    vacation_create: 'Urlaub beantragen',
    vacation_approve: 'Urlaub genehmigen',
    tools_view: 'Tools sehen',
    tools_edit: 'Tools bearbeiten',
    passwords_view: 'Passwörter sehen',
    passwords_edit: 'Passwörter bearbeiten',
    challenges_view: 'Challenges sehen',
    challenges_admin: 'Challenges verwalten',
    forms_view: 'Formulare sehen',
    forms_view_all: 'Alle Formulare sehen',
    forms_edit_all: 'Alle Formulare bearbeiten',
    forms_approve: 'Formulare genehmigen',
    forms_delete: 'Formulare löschen',
    benefits_view: 'Vorteilspass sehen',
    benefits_admin: 'Vorteilspass verwalten',
    calendar_view: 'Kalender sehen',
    chat_view: 'Chat sehen',
    team_view: 'Team Hub sehen',
    notes_view: 'Notizen sehen',
    social_faces_view: 'Social Faces sehen',
    system_settings: 'Systemeinstellungen'
};

const SystemPage = () => {
    const { api, user, startImpersonation } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('users');
    const [loading, setLoading] = useState(true);
    
    // Users
    const [users, setUsers] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({
        email: '', username: '', password: '', first_name: '', last_name: '',
        role: 'mitarbeiter', weekly_hours: 40, total_vacation_days: 30,
        remaining_vacation_days: 30, birthday: '', profile_color: '#009dde'
    });
    const [showHoursChange, setShowHoursChange] = useState(false);
    const [hoursChange, setHoursChange] = useState({ weekly_hours: 40, effective_from: '' });
    
    // Roles & Permissions
    const [roles, setRoles] = useState({});
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState('mitarbeiter');
    
    // Task Statuses
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [statusForm, setStatusForm] = useState({
        id: '', name: '', color: '#6b7280', emoji: '⚪', is_done: false
    });
    
    // Audit Logs
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditPage, setAuditPage] = useState(0);
    
    // System Settings
    const [settings, setSettings] = useState({
        logo_url: null, company_name: 'AgencyOS',
        primary_color: '#009dde', secondary_color: '#f5f5f7',
        accent_color: '#34c759', default_theme: 'system',
        activity_types: [], app_icon_url: null, app_url: '',
        challenges_enabled: true,
        chat_enabled: true,
        task_reminder_days: [1, 3, 7],
        package_fields: [
            'Postings META',
            'Postings TikTok',
            'Stories META',
            'Fotoproduktion',
            'HQ Videoproduktion',
            'UGC Videoproduktion',
            'Community Management',
            'Ad Management (exkl. Ad Budget)',
            'Reporting'
        ]
    });
    const [newActivityType, setNewActivityType] = useState('');
    const [newPackageField, setNewPackageField] = useState('');
    const [uploadingAppIcon, setUploadingAppIcon] = useState(false);
    
    const logoInputRef = useRef(null);
    const profileInputRef = useRef(null);
    const appIconInputRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [usersRes, rolesRes, settingsRes, statusesRes, auditRes] = await Promise.all([
                api.get('/system/users'),
                api.get('/system/roles'),
                api.get('/system/settings'),
                api.get('/system/task-statuses'),
                api.get('/system/audit-logs?limit=50')
            ]);
            setUsers(usersRes.data);
            setRoles(rolesRes.data.roles);
            setAvailablePermissions(rolesRes.data.available_permissions);
            setSettings(settingsRes.data);
            setTaskStatuses(statusesRes.data);
            setAuditLogs(auditRes.data.logs || []);
            setAuditTotal(auditRes.data.total || 0);
        } catch (error) {
            console.error('Failed to fetch system data:', error);
            toast.error('Fehler beim Laden der Systemdaten');
        } finally {
            setLoading(false);
        }
    };
    
    // Task Status Management
    const openCreateStatus = () => {
        setEditingStatus(null);
        setStatusForm({ id: '', name: '', color: '#6b7280', emoji: '⚪', is_done: false });
        setShowStatusModal(true);
    };
    
    const openEditStatus = (status) => {
        setEditingStatus(status);
        setStatusForm({
            id: status.id,
            name: status.name,
            color: status.color,
            emoji: status.emoji || '⚪',
            is_done: status.is_done || false
        });
        setShowStatusModal(true);
    };
    
    const handleSaveStatus = async () => {
        try {
            if (editingStatus) {
                await api.put(`/system/task-statuses/${editingStatus.id}`, {
                    name: statusForm.name,
                    color: statusForm.color,
                    emoji: statusForm.emoji,
                    is_done: statusForm.is_done
                });
                toast.success('Status aktualisiert');
            } else {
                const newId = statusForm.id || statusForm.name.toLowerCase().replace(/\s+/g, '_');
                await api.post('/system/task-statuses', {
                    ...statusForm,
                    id: newId,
                    order: taskStatuses.length
                });
                toast.success('Status erstellt');
            }
            setShowStatusModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };
    
    const handleDeleteStatus = async (statusId) => {
        if (!window.confirm('Status wirklich löschen?')) return;
        try {
            await api.delete(`/system/task-statuses/${statusId}`);
            toast.success('Status gelöscht');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    // User Management
    const openCreateUser = () => {
        setEditingUser(null);
        setUserForm({
            email: '', username: '', password: '', first_name: '', last_name: '',
            role: 'mitarbeiter', weekly_hours: 40, total_vacation_days: 30,
            remaining_vacation_days: 30, overtime_balance_minutes: 0,
            birthday: '', profile_color: '#009dde',
            display_title: '', employment_start_date: ''
        });
        setShowUserModal(true);
    };

    const openEditUser = (u) => {
        setEditingUser(u);
        setUserForm({
            email: u.email, username: u.username, password: '',
            first_name: u.first_name, last_name: u.last_name,
            role: u.role, weekly_hours: u.weekly_hours,
            weekly_hours_history: Array.isArray(u.weekly_hours_history) ? u.weekly_hours_history : [],
            weekly_hours_history_needs_review: !!u.weekly_hours_history_needs_review,
            total_vacation_days: u.total_vacation_days,
            remaining_vacation_days: u.remaining_vacation_days,
            overtime_balance_minutes: u.overtime_balance_minutes || 0,
            birthday: u.birthday || '', profile_color: u.profile_color,
            display_title: u.display_title || '',
            employment_start_date: u.employment_start_date || ''
        });
        setShowHoursChange(false);
        setHoursChange({ weekly_hours: u.weekly_hours || 40, effective_from: new Date().toISOString().split('T')[0] });
        setShowUserModal(true);
    };

    const handleSaveUser = async () => {
        try {
            if (editingUser) {
                // Update
                const updateData = { ...userForm };
                delete updateData.password;
                delete updateData.username;
                await api.put(`/system/users/${editingUser.id}`, updateData);
                toast.success('Benutzer aktualisiert');
            } else {
                // Create
                if (!userForm.password) {
                    // Send invite instead
                    const response = await api.post('/system/users', { ...userForm, send_invite: true });
                    if (response.data.invite_link) {
                        toast.success(
                            <div>
                                <p>Benutzer erstellt!</p>
                                <p className="text-xs mt-1">Einladungslink: {response.data.invite_link}</p>
                            </div>
                        );
                    }
                } else {
                    await api.post('/system/users', userForm);
                    toast.success('Benutzer erstellt');
                }
            }
            setShowUserModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Benutzer wirklich löschen? Alle zugehörigen Daten werden ebenfalls gelöscht.')) return;
        try {
            await api.delete(`/system/users/${userId}`);
            toast.success('Benutzer gelöscht');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleResetPassword = async (userId) => {
        const targetUser = users.find(u => u.id === userId);
        const name = targetUser ? `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim() : 'diesen Benutzer';
        if (!window.confirm(`Passwort von "${name}" wirklich zurücksetzen?\n\nDas aktuelle Passwort wird ungültig und ein neuer Einladungslink wird generiert.`)) return;
        try {
            const response = await api.post(`/system/users/${userId}/reset-password`);
            const data = response.data || {};
            const url = data.invite_url || `${window.location.origin}${data.invite_link || ''}`;
            setResetInfo({ name, url, emailSent: !!data.email_sent, emailError: data.email_error, email: data.email });
            if (data.email_sent) toast.success('Passwort zurückgesetzt, E-Mail versendet');
            else toast.warning('Passwort zurückgesetzt, aber keine E-Mail versendet');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
        }
    };

    const [resetInfo, setResetInfo] = useState(null);
    const copyResetLink = async () => {
        try {
            await navigator.clipboard.writeText(resetInfo.url);
            toast.success('Link kopiert');
        } catch (e) {
            toast.error('Kopieren nicht möglich, bitte Link markieren');
        }
    };

    const handleImpersonate = async (targetUser) => {
        const name = `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim();
        if (!window.confirm(`Als "${name}" einloggen?\n\nOben erscheint ein Banner, mit dem du jederzeit wieder zurück zum Admin-Account wechseln kannst.`)) return;
        try {
            const response = await api.post(`/system/users/${targetUser.id}/impersonate`);
            const { access_token, user: userData } = response.data || {};
            if (!access_token || !userData) {
                toast.error('Ungültige Antwort vom Server');
                return;
            }
            await startImpersonation(access_token, userData);
            toast.success(`Eingeloggt als ${name}`);
            // SPA navigation – avoids full reload + localStorage race condition
            navigate('/', { replace: true });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Impersonate');
        }
    };

    // Activity Types
    const handleAddActivityType = () => {
        if (!newActivityType.trim()) return;
        if (settings.activity_types?.includes(newActivityType.trim())) {
            toast.error('Aktivitätstyp existiert bereits');
            return;
        }
        const newTypes = [...(settings.activity_types || []), newActivityType.trim()];
        setSettings({ ...settings, activity_types: newTypes });
        setNewActivityType('');
    };

    const handleRemoveActivityType = (type) => {
        const newTypes = settings.activity_types.filter(t => t !== type);
        setSettings({ ...settings, activity_types: newTypes });
    };

    // Package Fields Handlers
    const handleAddPackageField = () => {
        if (!newPackageField.trim()) return;
        if (settings.package_fields?.includes(newPackageField.trim())) {
            toast.error('Feld existiert bereits');
            return;
        }
        const newFields = [...(settings.package_fields || []), newPackageField.trim()];
        setSettings({ ...settings, package_fields: newFields });
        setNewPackageField('');
    };

    const handleRemovePackageField = (field) => {
        const newFields = settings.package_fields.filter(f => f !== field);
        setSettings({ ...settings, package_fields: newFields });
    };

    // Permissions
    const handlePermissionChange = async (permission, value) => {
        const newPerms = { ...roles[selectedRole], [permission]: value };
        try {
            await api.put(`/system/roles/${selectedRole}`, newPerms);
            setRoles({ ...roles, [selectedRole]: newPerms });
            toast.success('Berechtigung aktualisiert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    // Settings
    const handleSaveSettings = async () => {
        try {
            await api.put('/system/settings', settings);
            toast.success('Einstellungen gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await api.post('/system/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSettings({ ...settings, logo_url: response.data.logo_url });
            toast.success('Logo hochgeladen');
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        }
    };
    
    const handleAppIconUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setUploadingAppIcon(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await api.post('/pwa/upload-icon', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSettings({ ...settings, app_icon_url: response.data.icon_url });
            toast.success('App-Icon hochgeladen! Die Änderung wird beim nächsten Hinzufügen zum Home-Bildschirm wirksam.');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Fehler beim Hochladen';
            toast.error(msg);
        } finally {
            setUploadingAppIcon(false);
        }
    };
    
    const handleDeleteAppIcon = async () => {
        try {
            await api.delete('/pwa/icon');
            setSettings({ ...settings, app_icon_url: null });
            toast.success('App-Icon zurückgesetzt');
        } catch (error) {
            toast.error('Fehler beim Zurücksetzen');
        }
    };

    if (user?.role !== 'chef') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Card className="p-8 text-center">
                    <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">Zugriff verweigert</h2>
                    <p className="text-muted-foreground mt-2">Nur Administratoren haben Zugriff auf diesen Bereich.</p>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="system-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        ⚙️ Systemverwaltung
                    </h1>
                    <p className="text-muted-foreground">Benutzer, Rollen und Einstellungen verwalten</p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                    <TabsTrigger value="users" data-testid="tab-users">
                        <Users className="w-4 h-4 mr-2" />
                        Benutzer
                    </TabsTrigger>
                    <TabsTrigger value="roles" data-testid="tab-roles">
                        <Shield className="w-4 h-4 mr-2" />
                        Rollen & Rechte
                    </TabsTrigger>
                    <TabsTrigger value="statuses" data-testid="tab-statuses">
                        <Settings className="w-4 h-4 mr-2" />
                        Task-Status
                    </TabsTrigger>
                    <TabsTrigger value="audit" data-testid="tab-audit">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Audit-Log
                    </TabsTrigger>
                    <TabsTrigger value="design" data-testid="tab-design">
                        <Palette className="w-4 h-4 mr-2" />
                        Design
                    </TabsTrigger>
                    <TabsTrigger value="stats" data-testid="tab-stats">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Statistiken
                    </TabsTrigger>
                    <TabsTrigger value="questionnaires" data-testid="tab-questionnaires">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Fragebogen
                    </TabsTrigger>
                    <TabsTrigger value="api" data-testid="tab-api">
                        <KeyRound className="w-4 h-4 mr-2" />
                        API-Zugriff
                    </TabsTrigger>
                </TabsList>

                {/* Users Tab */}
                <TabsContent value="users">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Benutzer ({users.length})</CardTitle>
                            <Button onClick={openCreateUser} data-testid="create-user-btn">
                                <UserPlus className="w-4 h-4 mr-2" />
                                Neuer Benutzer
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {users.map(u => (
                                    <motion.div
                                        key={u.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <Avatar className="w-12 h-12">
                                                {u.profile_image ? (
                                                    <AvatarImage src={u.profile_image} />
                                                ) : null}
                                                <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white">
                                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-medium">{u.first_name} {u.last_name}</p>
                                                <p className="text-sm text-muted-foreground">{u.email}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant={u.role === 'chef' ? 'default' : 'secondary'}>
                                                        {ROLE_LABELS[u.role] || u.role}
                                                    </Badge>
                                                    {!u.is_active && <Badge variant="destructive">Inaktiv</Badge>}
                                                    {u.needs_setup && <Badge variant="outline">Einrichtung ausstehend</Badge>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                            <div className="text-right hidden md:block">
                                                <p>{u.weekly_hours}h/Woche</p>
                                                <p>{u.remaining_vacation_days}/{u.total_vacation_days} Urlaubstage</p>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleResetPassword(u.id)} data-testid={`reset-pw-${u.id}`}>
                                                    <Key className="w-4 h-4" />
                                                </Button>
                                                {user?.username === 'admin' && u.id !== user?.id && u.is_active !== false && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleImpersonate(u)}
                                                        title={`Als ${u.first_name} ${u.last_name} einloggen`}
                                                        data-testid={`impersonate-${u.id}`}
                                                    >
                                                        <LogIn className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {u.id !== user?.id && (
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="text-destructive" data-testid={`delete-user-${u.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Roles Tab */}
                <TabsContent value="roles">
                    <Card>
                        <CardHeader>
                            <CardTitle>Rollen & Berechtigungen</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="mb-6">
                                <Label>Rolle auswählen</Label>
                                <Select value={selectedRole} onValueChange={setSelectedRole}>
                                    <SelectTrigger className="w-64 mt-2">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(ROLE_LABELS).map(role => (
                                            <SelectItem key={role} value={role}>
                                                {ROLE_LABELS[role]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {availablePermissions.map(perm => (
                                    <div key={perm} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                                        <span className="text-sm">{PERMISSION_LABELS[perm] || perm}</span>
                                        <Switch
                                            checked={roles[selectedRole]?.[perm] || false}
                                            onCheckedChange={(val) => handlePermissionChange(perm, val)}
                                            disabled={selectedRole === 'chef'}
                                        />
                                    </div>
                                ))}
                            </div>
                            {selectedRole === 'chef' && (
                                <p className="text-sm text-muted-foreground mt-4">
                                    ℹ️ Administrator-Rechte können nicht eingeschränkt werden.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Task Status Tab */}
                <TabsContent value="statuses">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Task-Status ({taskStatuses.length})</CardTitle>
                            <Button onClick={openCreateStatus} data-testid="create-status-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Neuer Status
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {taskStatuses.map((status, index) => (
                                    <motion.div
                                        key={status.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">{status.emoji || '⚪'}</span>
                                            <div 
                                                className="w-4 h-4 rounded-full"
                                                style={{ backgroundColor: status.color }}
                                            />
                                            <div>
                                                <p className="font-medium">{status.name}</p>
                                                <p className="text-xs text-muted-foreground">ID: {status.id}</p>
                                            </div>
                                            {status.is_done && (
                                                <Badge variant="outline" className="text-green-600 border-green-600">
                                                    <Check className="w-3 h-3 mr-1" />
                                                    Abschluss-Status
                                                </Badge>
                                            )}
                                            {status.is_default && (
                                                <Badge variant="outline">Standard</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditStatus(status)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteStatus(status.id)}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </motion.div>
                                ))}
                                {taskStatuses.length === 0 && (
                                    <p className="text-center text-muted-foreground py-8">
                                        Keine Status definiert
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Audit Log Tab */}
                <TabsContent value="audit">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Audit-Log ({auditTotal} Einträge)</CardTitle>
                            <Button variant="outline" size="sm" onClick={fetchData}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Aktualisieren
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {auditLogs.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">
                                    Noch keine Log-Einträge vorhanden
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {auditLogs.map((log, index) => (
                                        <motion.div
                                            key={log.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.02 }}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-sm"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Badge variant={
                                                    log.action === 'create' ? 'default' :
                                                    log.action === 'delete' ? 'destructive' :
                                                    log.action === 'update' ? 'secondary' :
                                                    'outline'
                                                }>
                                                    {log.action}
                                                </Badge>
                                                <div>
                                                    <p>
                                                        <span className="font-medium">{log.user_name || 'System'}</span>
                                                        {' '}{log.entity_type} {typeof log.entity_id === 'string' ? log.entity_id.substring(0, 8) + '...' : ''}
                                                    </p>
                                                    {log.details?.description && (
                                                        <p className="text-xs text-muted-foreground">{log.details.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(log.created_at).toLocaleString('de-DE')}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Design Tab */}
                <TabsContent value="design">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Logo & Branding</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div>
                                    <Label>Firmenname</Label>
                                    <Input
                                        value={settings.company_name}
                                        onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                                        className="mt-2"
                                    />
                                </div>
                                <div>
                                    <Label>App-URL / Custom Domain</Label>
                                    <Input
                                        value={settings.app_url || ''}
                                        onChange={(e) => setSettings({ ...settings, app_url: e.target.value })}
                                        placeholder="z.B. https://app.deinefirma.at"
                                        className="mt-2"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Wird fuer Links in E-Mails verwendet (Passwort-Reset, Einladungen, etc.)
                                    </p>
                                    {!settings.app_url && (
                                        <p className="text-xs text-amber-600 mt-1 font-medium">
                                            Bitte setze die Domain damit E-Mail-Links korrekt sind!
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label>Logo</Label>
                                    <div className="mt-2 flex items-center gap-4">
                                        {settings.logo_url ? (
                                            <img 
                                                src={settings.logo_url} 
                                                alt="Logo" 
                                                className="h-16 w-auto object-contain rounded-lg border bg-white p-2"
                                            />
                                        ) : (
                                            <div className="h-16 w-16 rounded-lg bg-primary flex items-center justify-center">
                                                <span className="text-white text-2xl font-bold">A</span>
                                            </div>
                                        )}
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleLogoUpload}
                                        />
                                        <Button variant="outline" onClick={() => logoInputRef.current?.click()}>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Logo hochladen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* App Icon for PWA */}
                                <div className="pt-4 border-t">
                                    <Label>App-Icon (für Home-Bildschirm)</Label>
                                    <p className="text-xs text-muted-foreground mt-1 mb-3">
                                        Quadratisches Icon für iOS/Android wenn die App zum Home-Bildschirm hinzugefügt wird.
                                    </p>
                                    <div className="mt-2 flex items-center gap-4">
                                        {settings.app_icon_url ? (
                                            <img 
                                                src={settings.app_icon_url} 
                                                alt="App Icon" 
                                                className="h-16 w-16 object-contain rounded-2xl border shadow-sm"
                                            />
                                        ) : (
                                            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-sm">
                                                <span className="text-white text-xl font-bold">A</span>
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-2">
                                            <input
                                                ref={appIconInputRef}
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg"
                                                className="hidden"
                                                onChange={handleAppIconUpload}
                                            />
                                            <Button 
                                                variant="outline" 
                                                onClick={() => appIconInputRef.current?.click()}
                                                disabled={uploadingAppIcon}
                                            >
                                                {uploadingAppIcon ? (
                                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Upload className="w-4 h-4 mr-2" />
                                                )}
                                                {uploadingAppIcon ? 'Wird hochgeladen...' : 'App-Icon hochladen'}
                                            </Button>
                                            {settings.app_icon_url && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    className="text-red-500 hover:text-red-600"
                                                    onClick={handleDeleteAppIcon}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-1" />
                                                    Zurücksetzen
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Farben</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <Label>Primärfarbe</Label>
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                type="color"
                                                value={settings.primary_color}
                                                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                                                className="w-10 h-10 rounded cursor-pointer"
                                            />
                                            <Input
                                                value={settings.primary_color}
                                                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                                                className="font-mono"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Sekundärfarbe</Label>
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                type="color"
                                                value={settings.secondary_color}
                                                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                                                className="w-10 h-10 rounded cursor-pointer"
                                            />
                                            <Input
                                                value={settings.secondary_color}
                                                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                                                className="font-mono"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Akzentfarbe</Label>
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                type="color"
                                                value={settings.accent_color}
                                                onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                                                className="w-10 h-10 rounded cursor-pointer"
                                            />
                                            <Input
                                                value={settings.accent_color}
                                                onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                                                className="font-mono"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Label>Standard-Theme</Label>
                                    <Select 
                                        value={settings.default_theme} 
                                        onValueChange={(val) => setSettings({ ...settings, default_theme: val })}
                                    >
                                        <SelectTrigger className="mt-2">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="system">System (Auto)</SelectItem>
                                            <SelectItem value="light">Hell</SelectItem>
                                            <SelectItem value="dark">Dunkel</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Task-Erinnerungen: Vorlauftage */}
                                <div className="mt-4">
                                    <Label>Task-Erinnerungen (Vorlauftage)</Label>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        An wie vielen Tagen vor der Fälligkeit soll erinnert werden (E-Mail + Push)?
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                                            const active = (settings.task_reminder_days || []).includes(d);
                                            return (
                                                <button
                                                    key={d}
                                                    type="button"
                                                    onClick={() => {
                                                        const cur = settings.task_reminder_days || [];
                                                        const next = active ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b);
                                                        setSettings({ ...settings, task_reminder_days: next });
                                                    }}
                                                    className={`w-11 h-11 rounded-xl border text-sm font-medium transition-colors ${active ? 'bg-primary text-white border-primary' : 'bg-muted/40 border-border text-muted-foreground hover:bg-muted'}`}
                                                >
                                                    {d} {d === 1 ? 'Tag' : 'Tage'}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Der Versand läuft automatisch täglich um 10:00 Uhr — für Tasks, die in genau so vielen Tagen fällig sind.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-xl mt-2"
                                        onClick={async () => {
                                            try {
                                                const res = await api.post('/tasks/test-reminder');
                                                toast.success(`Test verschickt (E-Mail: ${res.data.email}, Push: ${res.data.push})`);
                                            } catch {
                                                toast.error('Test-Erinnerung fehlgeschlagen');
                                            }
                                        }}
                                    >
                                        Test-Erinnerung an mich senden
                                    </Button>
                                </div>

                                <Button onClick={handleSaveSettings} className="w-full mt-4">
                                    <Save className="w-4 h-4 mr-2" />
                                    Einstellungen speichern
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Activity Types */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Aktivitätstypen (Zeiterfassung)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-2 mb-4">
                                    <Input
                                        value={newActivityType}
                                        onChange={(e) => setNewActivityType(e.target.value)}
                                        placeholder="Neuer Aktivitätstyp..."
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddActivityType()}
                                    />
                                    <Button onClick={handleAddActivityType}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Hinzufügen
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {settings.activity_types?.map(type => (
                                        <Badge key={type} variant="secondary" className="px-3 py-1.5 text-sm">
                                            {type}
                                            <button
                                                onClick={() => handleRemoveActivityType(type)}
                                                className="ml-2 hover:text-destructive"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                                {settings.activity_types?.length === 0 && (
                                    <p className="text-sm text-muted-foreground">Keine Aktivitätstypen definiert.</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-4">
                                    Diese Typen erscheinen beim Stoppen der Zeiterfassung als Dropdown-Auswahl.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Module Settings */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Module</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-base">Challenges</Label>
                                        <p className="text-sm text-muted-foreground">Team-Challenges und Achievements</p>
                                    </div>
                                    <Switch
                                        checked={settings.challenges_enabled !== false}
                                        onCheckedChange={async (checked) => {
                                            const newSettings = { ...settings, challenges_enabled: checked };
                                            setSettings(newSettings);
                                            try {
                                                await api.put('/system/settings', newSettings);
                                                toast.success(checked ? 'Challenges aktiviert' : 'Challenges deaktiviert');
                                            } catch (error) {
                                                toast.error('Fehler beim Speichern');
                                                setSettings({ ...settings, challenges_enabled: !checked });
                                            }
                                        }}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-base">Team-Chat</Label>
                                        <p className="text-sm text-muted-foreground">Interner Chat für das Team</p>
                                    </div>
                                    <Switch
                                        checked={settings.chat_enabled !== false}
                                        onCheckedChange={async (checked) => {
                                            const newSettings = { ...settings, chat_enabled: checked };
                                            setSettings(newSettings);
                                            try {
                                                await api.put('/system/settings', newSettings);
                                                toast.success(checked ? 'Chat aktiviert' : 'Chat deaktiviert');
                                            } catch (error) {
                                                toast.error('Fehler beim Speichern');
                                                setSettings({ ...settings, chat_enabled: !checked });
                                            }
                                        }}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-base">Projekte</Label>
                                        <p className="text-sm text-muted-foreground">Projekt-Verwaltung</p>
                                    </div>
                                    <Switch
                                        checked={settings.projects_enabled !== false}
                                        onCheckedChange={async (checked) => {
                                            const newSettings = { ...settings, projects_enabled: checked };
                                            setSettings(newSettings);
                                            try {
                                                await api.put('/system/settings', newSettings);
                                                toast.success(checked ? 'Projekte aktiviert' : 'Projekte deaktiviert');
                                            } catch (error) {
                                                toast.error('Fehler beim Speichern');
                                                setSettings({ ...settings, projects_enabled: !checked });
                                            }
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground border-t pt-3">
                                    Bei Deaktivierung wird das Modul im Menü ausgeblendet.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Package Fields */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Kunden-Paket Felder</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-2 mb-4">
                                    <Input
                                        value={newPackageField}
                                        onChange={(e) => setNewPackageField(e.target.value)}
                                        placeholder="Neues Feld hinzufügen..."
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddPackageField()}
                                    />
                                    <Button onClick={handleAddPackageField}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Hinzufügen
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {settings.package_fields?.map(field => (
                                        <Badge key={field} variant="secondary" className="px-3 py-1.5 text-sm">
                                            {field}
                                            <button
                                                onClick={() => handleRemovePackageField(field)}
                                                className="ml-2 hover:text-destructive"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                                {(!settings.package_fields || settings.package_fields.length === 0) && (
                                    <p className="text-sm text-muted-foreground">Keine Paket-Felder definiert.</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-4">
                                    Diese Felder können beim Kunden ausgefüllt werden (z.B. "Postings META: 8", "Fotoproduktion: 1x/Monat").
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Stats Tab */}
                <TabsContent value="stats">
                    <AdminTimeStats />
                </TabsContent>

                {/* Questionnaires Tab */}
                <TabsContent value="questionnaires">
                    <QuestionnaireAdmin />
                </TabsContent>

                <TabsContent value="api">
                    <BackupSection api={api} />
                    <div className="mt-6">
                        <StorageMigrationSection api={api} />
                    </div>
                    <div className="mt-6">
                        <ApiAccessSection api={api} />
                    </div>
                </TabsContent>
            </Tabs>

            {/* User Modal */}
            <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Vorname *</Label>
                            <Input
                                value={userForm.first_name}
                                onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Nachname *</Label>
                            <Input
                                value={userForm.last_name}
                                onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>E-Mail *</Label>
                            <Input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Benutzername *</Label>
                            <Input
                                value={userForm.username}
                                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                                disabled={!!editingUser}
                            />
                        </div>
                        {!editingUser && (
                            <div className="col-span-2">
                                <Label>Passwort</Label>
                                <Input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                    placeholder="Leer lassen für Einladungslink"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Wenn leer, wird ein Einladungslink generiert.
                                </p>
                            </div>
                        )}
                        <div>
                            <Label>Rolle</Label>
                            <Select value={userForm.role} onValueChange={(val) => {
                                // Reset time/vacation values for influencer/freelancer
                                if (val === 'influencer' || val === 'freelancer') {
                                    setUserForm({ ...userForm, role: val, weekly_hours: 0, total_vacation_days: 0, remaining_vacation_days: 0 });
                                } else {
                                    setUserForm({ ...userForm, role: val });
                                }
                            }}>
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(ROLE_LABELS).map(([val, label]) => (
                                        <SelectItem key={val} value={val}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(userForm.role === 'influencer' || userForm.role === 'freelancer') && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    {userForm.role === 'influencer' 
                                        ? '👤 Sieht nur Vorteilspass' 
                                        : '💼 Ohne Zeiterfassung & Urlaub'}
                                </p>
                            )}
                            {editingUser && editingUser.permissions && (
                                <div className="mt-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 flex items-start gap-2">
                                    <div className="text-xs text-amber-900 dark:text-amber-200 flex-1">
                                        <p className="font-medium">⚠ Individuelle Berechtigungen aktiv</p>
                                        <p>Dieser Benutzer hat eigene Berechtigungs-Überschreibungen, die die Rollen-Defaults überschreiben. Falls etwas nicht sichtbar ist (z.B. Social Faces), nutze "Auf Rollen-Default zurücksetzen".</p>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                            if (!window.confirm('Berechtigungen wirklich auf den Rollen-Default zurücksetzen?')) return;
                                            try {
                                                await api.post(`/system/users/${editingUser.id}/reset-permissions`);
                                                toast.success('Berechtigungen zurückgesetzt');
                                                setShowUserModal(false);
                                                fetchData();
                                            } catch (e) {
                                                toast.error(e.response?.data?.detail || 'Fehler beim Zurücksetzen');
                                            }
                                        }}
                                        data-testid="reset-permissions-btn"
                                    >
                                        Auf Default zurücksetzen
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Anzeige-Position</Label>
                            <Input
                                value={userForm.display_title}
                                onChange={(e) => setUserForm({ ...userForm, display_title: e.target.value })}
                                placeholder="z.B. Motion-Designer, Projektmanager..."
                                data-testid="display-title-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Wird statt der Rolle angezeigt (optional)</p>
                        </div>
                        <div>
                            <Label>Profilfarbe</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <input
                                    type="color"
                                    value={userForm.profile_color}
                                    onChange={(e) => setUserForm({ ...userForm, profile_color: e.target.value })}
                                    className="w-10 h-10 rounded cursor-pointer"
                                />
                                <Input
                                    value={userForm.profile_color}
                                    onChange={(e) => setUserForm({ ...userForm, profile_color: e.target.value })}
                                    className="font-mono"
                                />
                            </div>
                        </div>
                        {/* Hide time/vacation fields for influencer and freelancer */}
                        {userForm.role !== 'influencer' && userForm.role !== 'freelancer' && (
                        <>
                        <div className="col-span-2">
                            <Label>Wochenstunden (aktuell)</Label>
                            <Input
                                type="number"
                                value={userForm.weekly_hours}
                                onChange={(e) => setUserForm({ ...userForm, weekly_hours: parseFloat(e.target.value) })}
                                data-testid="weekly-hours-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Diese Zahl ist die aktuelle Sollvorgabe. Für rückwirkungsfreie Änderungen nutze "Stunden ab Datum ändern" unten.
                            </p>

                            {/* Hours history */}
                            {editingUser && (
                                <div className="mt-3 rounded-xl border border-border/60 p-3 bg-muted/30 space-y-2" data-testid="weekly-hours-history">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs font-medium">Stunden-Historie</Label>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowHoursChange(v => !v)}
                                            className="h-7 gap-1"
                                            data-testid="toggle-hours-change"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            {showHoursChange ? 'Abbrechen' : 'Stunden ab Datum ändern'}
                                        </Button>
                                    </div>

                                    {userForm.weekly_hours_history_needs_review && (
                                        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-900 dark:text-amber-200 space-y-1" data-testid="hours-history-review-warning">
                                            <p className="font-semibold">⚠ Bitte alten Wochenstunden-Wert prüfen</p>
                                            <p>Beim Migrieren wurde ein Anfangs-Eintrag automatisch hinzugefügt — er entspricht dem ersten existierenden Wert. Falls der Mitarbeiter VORHER andere Stunden hatte (z.B. 20h statt 10h), bitte den ältesten Eintrag löschen und mit "Stunden ab Datum ändern" den korrekten alten Wert ab Einstiegsdatum setzen.</p>
                                        </div>
                                    )}

                                    {showHoursChange && (
                                        <div className="rounded-lg border border-border/60 p-3 bg-background space-y-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <Label className="text-xs">Neue Wochenstunden</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        value={hoursChange.weekly_hours}
                                                        onChange={(e) => setHoursChange({ ...hoursChange, weekly_hours: parseFloat(e.target.value) || 0 })}
                                                        className="mt-1"
                                                        data-testid="hours-change-value"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-xs">Gültig ab</Label>
                                                    <Input
                                                        type="date"
                                                        value={hoursChange.effective_from}
                                                        onChange={(e) => setHoursChange({ ...hoursChange, effective_from: e.target.value })}
                                                        className="mt-1"
                                                        data-testid="hours-change-from"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (!hoursChange.effective_from) {
                                                            toast.error('Bitte ein Datum auswählen');
                                                            return;
                                                        }
                                                        const newEntry = {
                                                            effective_from: hoursChange.effective_from,
                                                            weekly_hours: parseFloat(hoursChange.weekly_hours) || 0,
                                                        };
                                                        let history = (userForm.weekly_hours_history || []).filter(h => h.effective_from !== newEntry.effective_from);

                                                        // First history entry: capture the OLD weekly_hours starting from the
                                                        // employment_start_date (or a very early fallback) so periods BEFORE
                                                        // the new entry are calculated with the previous value.
                                                        if (history.length === 0) {
                                                            const oldValue = parseFloat(userForm.weekly_hours) || 0;
                                                            if (oldValue && oldValue !== newEntry.weekly_hours) {
                                                                const baseDate = userForm.employment_start_date || '2000-01-01';
                                                                if (baseDate < newEntry.effective_from) {
                                                                    history.push({
                                                                        effective_from: baseDate,
                                                                        weekly_hours: oldValue,
                                                                    });
                                                                }
                                                            }
                                                        }

                                                        const updated = [...history, newEntry].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
                                                        setUserForm({
                                                            ...userForm,
                                                            weekly_hours_history: updated,
                                                            weekly_hours: newEntry.weekly_hours,
                                                            weekly_hours_history_needs_review: false,
                                                        });
                                                        setShowHoursChange(false);
                                                        toast.success('Eintrag hinzugefügt. Mit "Speichern" anwenden.');
                                                    }}
                                                    data-testid="hours-change-add"
                                                >
                                                    Hinzufügen
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* History list */}
                                    {(userForm.weekly_hours_history || []).length === 0 ? (
                                        <p className="text-xs text-muted-foreground">
                                            Keine Historie. Aktuell gelten {userForm.weekly_hours}h/Woche für den gesamten Zeitraum.
                                        </p>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            {(userForm.weekly_hours_history || []).map((h) => (
                                                <li
                                                    key={h.effective_from}
                                                    className="flex items-center justify-between rounded-lg bg-background border border-border/40 px-3 py-1.5 text-sm"
                                                    data-testid={`hours-history-${h.effective_from}`}
                                                >
                                                    <span>
                                                        <span className="font-medium">{h.weekly_hours}h/Woche</span>
                                                        <span className="text-muted-foreground"> ab {h.effective_from}</span>
                                                    </span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-destructive"
                                                        onClick={() => {
                                                            setUserForm({
                                                                ...userForm,
                                                                weekly_hours_history: (userForm.weekly_hours_history || []).filter(x => x.effective_from !== h.effective_from),
                                                                weekly_hours_history_needs_review: false,
                                                            });
                                                        }}
                                                        data-testid={`hours-history-del-${h.effective_from}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Einstiegsdatum</Label>
                            <Input
                                type="date"
                                value={userForm.employment_start_date || ''}
                                onChange={(e) => setUserForm({ ...userForm, employment_start_date: e.target.value })}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Urlaubstage werden jährlich an diesem Datum aufgefüllt
                            </p>
                        </div>
                        </>
                        )}
                        <div>
                            <Label>Geburtstag (MM-TT)</Label>
                            <Input
                                value={userForm.birthday}
                                onChange={(e) => setUserForm({ ...userForm, birthday: e.target.value })}
                                placeholder="z.B. 04-21"
                            />
                        </div>
                        {/* Hide vacation fields for influencer and freelancer */}
                        {userForm.role !== 'influencer' && userForm.role !== 'freelancer' && (
                        <>
                        <div>
                            <Label>Urlaubstage gesamt</Label>
                            <Input
                                type="number"
                                value={userForm.total_vacation_days}
                                onChange={(e) => setUserForm({ ...userForm, total_vacation_days: parseInt(e.target.value) })}
                            />
                        </div>
                        <div>
                            <Label>Resturlaub</Label>
                            <Input
                                type="number"
                                value={userForm.remaining_vacation_days}
                                onChange={(e) => setUserForm({ ...userForm, remaining_vacation_days: parseInt(e.target.value) })}
                            />
                        </div>
                        <div>
                            <Label>Überstunden-Korrektur (Minuten)</Label>
                            <Input
                                type="number"
                                value={userForm.overtime_balance_minutes || 0}
                                onChange={(e) => setUserForm({ ...userForm, overtime_balance_minutes: parseInt(e.target.value) || 0 })}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Manuelle Anpassung zum berechneten Zeitsaldo.<br/>
                                {(userForm.overtime_balance_minutes || 0) >= 0 
                                    ? `+${Math.floor((userForm.overtime_balance_minutes || 0) / 60)}h ${Math.abs((userForm.overtime_balance_minutes || 0) % 60)}m werden zum Saldo addiert`
                                    : `${Math.floor((userForm.overtime_balance_minutes || 0) / 60)}h ${Math.abs((userForm.overtime_balance_minutes || 0) % 60)}m werden vom Saldo abgezogen`
                                }
                            </p>
                            <p className="text-xs text-blue-500 mt-1">
                                Tipp: Hat der Mitarbeiter z.B. -5h Minusstunden, setze hier +300 Minuten, um auf 0 zu kommen.
                            </p>
                        </div>
                        </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUserModal(false)}>Abbrechen</Button>
                        <Button 
                            onClick={handleSaveUser}
                            disabled={!userForm.first_name || !userForm.last_name || !userForm.email || !userForm.username}
                        >
                            {editingUser ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Task Status Modal */}
            <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingStatus ? 'Status bearbeiten' : 'Neuer Status'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!editingStatus && (
                            <div>
                                <Label>Status-ID (eindeutig, ohne Leerzeichen)</Label>
                                <Input
                                    value={statusForm.id}
                                    onChange={(e) => setStatusForm({ ...statusForm, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                    placeholder="z.B. in_review"
                                    className="mt-1"
                                />
                            </div>
                        )}
                        <div>
                            <Label>Name</Label>
                            <Input
                                value={statusForm.name}
                                onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
                                placeholder="z.B. In Review"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Emoji</Label>
                            <Input
                                value={statusForm.emoji}
                                onChange={(e) => setStatusForm({ ...statusForm, emoji: e.target.value })}
                                placeholder="z.B. 🔵"
                                className="mt-1"
                                maxLength={2}
                            />
                            <p className="text-xs text-muted-foreground mt-1">Ein Emoji als Symbol für den Status</p>
                        </div>
                        <div>
                            <Label>Farbe</Label>
                            <div className="flex items-center gap-3 mt-1">
                                <input
                                    type="color"
                                    value={statusForm.color}
                                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                                    className="w-10 h-10 rounded-lg cursor-pointer border-0"
                                />
                                <Input
                                    value={statusForm.color}
                                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Abschluss-Status</Label>
                                <p className="text-xs text-muted-foreground">Markiert Tasks als erledigt</p>
                            </div>
                            <Switch
                                checked={statusForm.is_done}
                                onCheckedChange={(checked) => setStatusForm({ ...statusForm, is_done: checked })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowStatusModal(false)}>Abbrechen</Button>
                        <Button 
                            onClick={handleSaveStatus}
                            disabled={!statusForm.name || (!editingStatus && !statusForm.id)}
                        >
                            {editingStatus ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SystemPage;

// Questionnaire Admin Component
const QuestionnaireAdmin = () => {
    const { api } = useAuth();
    const [questionnaires, setQuestionnaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQ, setSelectedQ] = useState(null);

    useEffect(() => {
        api.get('/admin/questionnaires').then(res => {
            setQuestionnaires(res.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [api]);

    if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6" data-testid="questionnaire-admin">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">Mitarbeiter-Fragebogen</h3>
                    <p className="text-sm text-muted-foreground">{questionnaires.length} ausgefüllt</p>
                </div>
            </div>

            {questionnaires.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">Noch keine Fragebogen ausgefüllt.</CardContent></Card>
            ) : (
                <div className="space-y-3">
                    {questionnaires.map(q => (
                        <Card key={q.user_id} className="cursor-pointer hover:shadow-md transition-all" onClick={() => setSelectedQ(selectedQ?.user_id === q.user_id ? null : q)}>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <Avatar className="w-10 h-10">
                                        {q.user?.profile_image ? (
                                            <AvatarImage src={q.user.profile_image} />
                                        ) : (
                                            <AvatarFallback style={{ backgroundColor: q.user?.profile_color || '#009dde' }} className="text-white text-xs">
                                                {(q.user?.first_name?.[0] || '') + (q.user?.last_name?.[0] || '')}
                                            </AvatarFallback>
                                        )}
                                    </Avatar>
                                    <div className="flex-1">
                                        <p className="font-medium">{q.user?.first_name} {q.user?.last_name}</p>
                                        <p className="text-xs text-muted-foreground">{q.submitted_at ? new Date(q.submitted_at).toLocaleDateString('de-DE') : ''}</p>
                                    </div>
                                    <Badge variant="outline" className="text-green-600 border-green-200">Ausgefüllt</Badge>
                                </div>

                                {selectedQ?.user_id === q.user_id && (
                                    <div className="mt-4 pt-4 border-t space-y-3">
                                        {[
                                            { label: 'Branchen', value: q.branches },
                                            { label: 'Stärken', value: q.strengths },
                                            { label: 'Schwächen', value: q.weaknesses },
                                            { label: 'Wichtig bei der Arbeit', value: q.work_values },
                                            { label: 'Lernen / Können', value: q.learn_goals },
                                            { label: 'Social Media', value: q.social_media?.join(', ') },
                                            { label: 'Hobbys', value: q.hobbies }
                                        ].map((item, i) => (
                                            <div key={i}>
                                                <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
                                                <p className="text-sm mt-0.5">{item.value || '-'}</p>
                                            </div>
                                        ))}
                                        {q.favorite_profiles?.length > 0 && (
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground mb-1">Gute Profile</p>
                                                {q.favorite_profiles.map((p, i) => (
                                                    <div key={i} className="mb-1.5">
                                                        <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline break-all">{p.link}</a>
                                                        <p className="text-xs text-muted-foreground">{p.reason}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};


// ---------------------------------------------------------------------------
// API-Zugriff (externe Tools holen Daten per API-Key ab)
// ---------------------------------------------------------------------------

const ApiAccessSection = ({ api }) => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newKeyName, setNewKeyName] = useState('');
    const [creating, setCreating] = useState(false);
    const [revealedKey, setRevealedKey] = useState(null); // { name, raw_key }
    const [confirmRevoke, setConfirmRevoke] = useState(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const endpointList = `${backendUrl}/api/external/social-faces`;
    const endpointDetail = `${backendUrl}/api/external/social-faces/{id}`;

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api-keys');
            setKeys(res.data || []);
        } catch (e) {
            toast.error('Konnte Keys nicht laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const createKey = async () => {
        const name = newKeyName.trim();
        if (!name) { toast.error('Bitte einen Namen vergeben'); return; }
        setCreating(true);
        try {
            const res = await api.post('/api-keys', { name });
            setRevealedKey(res.data);
            setNewKeyName('');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler beim Erstellen');
        } finally {
            setCreating(false);
        }
    };

    const revokeKey = async (keyId) => {
        try {
            await api.delete(`/api-keys/${keyId}`);
            toast.success('Key widerrufen');
            setConfirmRevoke(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler beim Widerrufen');
        }
    };

    const copy = (text, label = 'Kopiert') => {
        navigator.clipboard.writeText(text);
        toast.success(label);
    };

    const curlExample = `curl -H "X-API-Key: ${revealedKey?.raw_key || 'DEIN_API_KEY'}" \\
     "${endpointList}"`;

    return (
        <div className="space-y-6" data-testid="api-access-section">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <KeyRound className="w-5 h-5" />
                        Social-Faces API
                    </CardTitle>
                    <CardDescription>
                        Externe Tools (z. B. Rechnungssystem) können hier per API-Key alle Social Faces mit
                        <strong> Name, E-Mail und Telefonnummer</strong> abgleichen. Nur lesend, keine internen Daten.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Endpoint documentation */}
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Endpoint (Liste aller Faces)
                            </div>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-background px-3 py-2 rounded border font-mono break-all">
                                    GET {endpointList}
                                </code>
                                <Button size="sm" variant="outline" onClick={() => copy(endpointList, 'Endpoint kopiert')} data-testid="copy-endpoint">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Endpoint (Einzelner Face nach ID)
                            </div>
                            <code className="text-xs bg-background px-3 py-2 rounded border font-mono block break-all">
                                GET {endpointDetail}
                            </code>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Header
                            </div>
                            <code className="text-xs bg-background px-3 py-2 rounded border font-mono block">
                                X-API-Key: sfk_...
                            </code>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Optionale Suche (Query-Parameter)
                            </div>
                            <code className="text-xs bg-background px-3 py-2 rounded border font-mono block break-all">
                                ?q=maria     &nbsp;→ sucht in Vorname, Nachname, E-Mail, Telefon
                            </code>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Beispiel-Antwort
                            </div>
                            <pre className="text-xs bg-background p-3 rounded border font-mono overflow-x-auto">{`{
  "total": 1234,
  "items": [
    {
      "id": "abc-123",
      "name": "Maria Muster",
      "first_name": "Maria",
      "last_name": "Muster",
      "email": "maria@example.com",
      "phone": "+43 660 1234567"
    }
  ]
}`}</pre>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                curl-Beispiel
                            </div>
                            <div className="flex items-start gap-2">
                                <pre className="flex-1 text-xs bg-background p-3 rounded border font-mono overflow-x-auto">{curlExample}</pre>
                                <Button size="sm" variant="outline" onClick={() => copy(curlExample, 'curl kopiert')} data-testid="copy-curl">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Create key */}
                    <div className="flex gap-2 items-end pt-2">
                        <div className="flex-1">
                            <Label className="text-xs">Neuen API-Key erstellen</Label>
                            <Input
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                placeholder="z. B. Rechnungstool"
                                data-testid="new-key-name"
                            />
                        </div>
                        <Button onClick={createKey} disabled={creating || !newKeyName.trim()} data-testid="create-key-btn">
                            <Plus className="w-4 h-4 mr-1" />
                            Erstellen
                        </Button>
                    </div>

                    {/* Key list */}
                    <div>
                        <Label className="text-xs">Vorhandene Keys</Label>
                        {loading ? (
                            <p className="text-sm text-muted-foreground mt-2">Lade…</p>
                        ) : keys.length === 0 ? (
                            <p className="text-sm text-muted-foreground mt-2">Noch keine Keys erstellt.</p>
                        ) : (
                            <div className="mt-2 space-y-2">
                                {keys.map(k => (
                                    <div key={k.id} className={`flex items-center justify-between p-3 border rounded-lg ${k.revoked_at ? 'opacity-60 bg-muted/30' : ''}`} data-testid={`api-key-row-${k.id}`}>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium">{k.name}</span>
                                                <code className="text-xs font-mono text-muted-foreground">{k.prefix}</code>
                                                {k.revoked_at && <Badge variant="outline" className="text-xs">widerrufen</Badge>}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                Erstellt {k.created_at?.slice(0, 10)}
                                                {k.created_by_name && ` · von ${k.created_by_name}`}
                                                {k.last_used_at && ` · Zuletzt genutzt ${k.last_used_at.slice(0, 10)}`}
                                                {!k.last_used_at && !k.revoked_at && ' · Noch nie genutzt'}
                                            </div>
                                        </div>
                                        {!k.revoked_at && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => setConfirmRevoke(k)}
                                                data-testid={`revoke-key-${k.id}`}
                                            >
                                                <Trash className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* One-time reveal dialog */}
            <Dialog open={!!revealedKey} onOpenChange={(o) => { if (!o) setRevealedKey(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>API-Key: {revealedKey?.name}</DialogTitle>
                        <DialogDescription>
                            Bitte jetzt kopieren — nach dem Schließen ist der Key <strong>nicht mehr sichtbar</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs font-mono bg-muted p-3 rounded break-all">
                                {revealedKey?.raw_key}
                            </code>
                            <Button size="sm" variant="outline" onClick={() => copy(revealedKey?.raw_key || '', 'Key kopiert')} data-testid="copy-raw-key">
                                <Copy className="w-4 h-4" />
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            In das externe Tool als Header <code>X-API-Key</code> eintragen.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setRevealedKey(null)} data-testid="close-revealed-key">
                            <Check className="w-4 h-4 mr-2" />
                            Habe ich gespeichert
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Revoke confirm */}
            <Dialog open={!!confirmRevoke} onOpenChange={(o) => { if (!o) setConfirmRevoke(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Key widerrufen?</DialogTitle>
                        <DialogDescription>
                            &bdquo;{confirmRevoke?.name}&ldquo; wird sofort ungültig. Externe Tools, die diesen Key nutzen, können anschließend nicht mehr zugreifen.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Abbrechen</Button>
                        <Button variant="destructive" onClick={() => revokeKey(confirmRevoke.id)} data-testid="confirm-revoke">
                            Widerrufen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Passwort zurueckgesetzt: Link + Mailstatus */}
            <Dialog open={!!resetInfo} onOpenChange={(v) => { if (!v) setResetInfo(null); }}>
                <DialogContent className="max-w-lg" data-testid="reset-password-dialog">
                    <DialogHeader>
                        <DialogTitle>Passwort zurückgesetzt</DialogTitle>
                        <DialogDescription>
                            {resetInfo?.name} kann über diesen Link ein neues Passwort festlegen. Der Link gilt 7 Tage.
                        </DialogDescription>
                    </DialogHeader>
                    {resetInfo && (
                        <div className="space-y-3">
                            {resetInfo.emailSent ? (
                                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-sm">
                                    E-Mail wurde an <strong>{resetInfo.email}</strong> gesendet.
                                </div>
                            ) : (
                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm space-y-1">
                                    <p><strong>Keine E-Mail versendet.</strong> Bitte den Link direkt weitergeben.</p>
                                    {resetInfo.emailError && <p className="text-xs text-muted-foreground">Grund: {resetInfo.emailError}</p>}
                                    <p className="text-xs text-muted-foreground">Mailversand testen: Einstellungen › Benachrichtigungen › Test-E-Mail.</p>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Input readOnly value={resetInfo.url} onFocus={(e) => e.target.select()} className="text-xs" data-testid="reset-link-input" />
                                <Button variant="outline" onClick={copyResetLink} data-testid="reset-link-copy">Kopieren</Button>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setResetInfo(null)}>Schließen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
